import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { usePatient360Ctx } from '../PatientWorkspaceLayout';
import { usePatient360ModuleAvailability, type Patient360ModuleKey } from '../../hooks/usePatient360ModuleAvailability';

interface Patient360ModuleGuardProps {
  moduleKey: Patient360ModuleKey;
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Shared guard for Patient360 feature routes that depend on module readiness.
 *
 * This keeps per-page guards consistent and avoids duplicated access logic.
 */
export function Patient360ModuleGuard({ moduleKey, children, redirectTo }: Patient360ModuleGuardProps) {
  const { programId, patientId } = usePatient360Ctx();
  const { modules, isLoading } = usePatient360ModuleAvailability(programId, {
    enabled: Boolean(programId),
  });

  const moduleState = modules[moduleKey];

  if (isLoading || moduleState?.isLoading) {
    return null;
  }

  if (!moduleState?.isAvailable) {
    const fallback = redirectTo ?? `/programs/${programId}/patients/${patientId}/360`;
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
